import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import './ClientReviews.scss';
import StarRating from './components/StarRating';
import { FaPaperPlane, FaStar, FaThumbsDown } from 'react-icons/fa';
import { createClientReview } from '../../../crud/clients_api/client_reviews';
import { getPaymentReceiptForReview } from '../../../crud/payments_api/payment_receipts';
import { FaArrowRotateRight } from 'react-icons/fa6';
import MiniLoadingSpinner from '../../../components/Spinners/MiniLoadingSpinner';
import { Icon } from '../../../components/common/IconLibrary';

interface Receipt {
  selections: any[];
}

interface IReviewSection {
  rating: number;
  comment: string;
}

interface ISaborReview extends IReviewSection {
  favorite?: any; 
  worst?: any;    
}

export interface IReviewData {
  amabilidad: IReviewSection;
  sabor: ISaborReview;
  experiencia: IReviewSection;
}

const ClientReviews: React.FC = () => {
  const [receipt, setReceipt] = useState<Receipt | undefined>(undefined);
  const { receipt_id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirect');

  const [amabilidadRating, setAmabilidadRating] = useState<number>(0);
  const [saborRating, setSaborRating] = useState<number>(0);
  const [experienciaRating, setExperienciaRating] = useState<number>(0);

  const [amabilidadComment, setAmabilidadComment] = useState<string>('');
  const [saborComment, setSaborComment] = useState<string>('');
  const [experienciaComment, setExperienciaComment] = useState<string>('');

  const [selectedFavorite, setSelectedFavorite] = useState<number | null>(null);
  const [selectedWorst, setSelectedWorst] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);

  // 1) Cargar el recibo para mostrar las selecciones de “Sabor”
  useEffect(() => {
    getPaymentReceiptForReview(Number(receipt_id))
      .then((response) => {
        if (response.status === 200) {
          setReceipt(response.data);
        } else {
          setError(true);
        }
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [receipt_id]);

  // 2) Construye la línea que combina nombre + opcionales (grupos / modificadores)
  function getSelectionLine(sel: any) {
    let result = sel.item_name;
    const details: string[] = [];

    if (sel.selection_groups && sel.selection_groups.length > 0) {
      const selectionNames = sel.selection_groups.flatMap((sg: any) =>
        sg.selected_items.map((sgi: any) => sgi.item_name)
      );
      if (selectionNames.length > 0) {
        details.push(selectionNames.join(', '));
      }
    }

    if (sel.modifiers && sel.modifiers.length > 0) {
      const modifierNames = sel.modifiers.map((mod: any) => mod.option_name);
      if (modifierNames.length > 0) {
        details.push(modifierNames.join(', '));
      }
    }

    if (details.length > 0) {
      result += ', ' + details.join(', ');
    }
    return result;
  }

  // 3) Arma el payload y llama al API
  const handleSubmit = () => {
    if (!receipt) return;
    setLoading(true);

    const saborReview: ISaborReview = {
      rating: saborRating,
      comment: saborComment,
    };

    // Solo agregamos “favorite” o “worst” si aplica:
    if (saborRating >= 4 && selectedFavorite !== null) {
      saborReview.favorite = receipt.selections[selectedFavorite];
    } else if (saborRating > 0 && saborRating <= 3 && selectedWorst !== null) {
      saborReview.worst = receipt.selections[selectedWorst];
    }

    const reviewData: IReviewData = {
      amabilidad:  { rating: amabilidadRating,  comment: amabilidadComment },
      sabor:       saborReview,
      experiencia: { rating: experienciaRating, comment: experienciaComment },
    };

    createClientReview(null, Number(receipt_id), reviewData)
      .then(() => {
        setSubmitted(true);
        // Redirect after 3 seconds if redirect URL is provided
        if (redirectUrl) {
          setTimeout(() => {
            navigate(redirectUrl);
          }, 3000);
        }
      })
      .catch(() => {
        setError(true);
        setSubmitted(false);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const renderThanksPage = () => (
    <div className="thank-section">
      <div className="centered-icon">
        <Icon iconName="check" iconColor="orange" size={64} />
      </div>
      <h1>¡Gracias por tu tiempo!</h1>
      <p>Tu opinión es muy valiosa para nosotros y nos ayuda a mejorar cada día. Esperamos verte pronto.</p>
      {redirectUrl && (
        <p className="redirect-message">Regresando al portal en unos momentos...</p>
      )}
    </div>
  );

  const renderErrorPage = () => {
    function resetError() {
      setError(false);
      setSubmitted(false);
      setLoading(false);
    }

    return (
      <div className="thank-section">
        <div className="centered-icon">
          <Icon iconName="cancel" iconColor="orange" size={64} />
        </div>
        <h1>¡Ups! Algo salió mal</h1>
        <p>No pudimos enviar tu reseña. Por favor, inténtalo nuevamente.</p>
        <button
          onClick={resetError}
          className="submit-button retry-button"
        >
          Volver a intentar <FaArrowRotateRight />
        </button>
      </div>
    );
  };

  // → Solo permitimos el envío si cada rating ha sido seleccionado (> 0)
  const canSubmit =
    amabilidadRating > 0 &&
    saborRating > 0 &&
    experienciaRating > 0;

  return (
    <div className="client-reviews-container">
      <div className="client-review-card">
        {loading && <MiniLoadingSpinner left="10%" />}
        {error && renderErrorPage()}
        {submitted && !error && renderThanksPage()}

        {/* Si NO está loading, NO fue enviado y NO hay error, muestro el formulario */}
        {!loading && !submitted && !error && (
          <>
            <h1>¿Cómo estuvo tu experiencia?</h1>

            {/* === Amabilidad === */}
            <div className="review-section">
              <h2>Atención y Amabilidad</h2>
              <StarRating
                count={5}
                value={amabilidadRating}
                onChange={setAmabilidadRating}
              />
              {/* Aparece el textarea apenas se haga clic en alguna estrella */}
              {amabilidadRating > 0 && (
                <textarea
                  placeholder="Cuéntanos sobre la atención que recibiste..."
                  value={amabilidadComment}
                  onChange={(e) =>
                    setAmabilidadComment(e.target.value)
                  }
                />
              )}
            </div>

            {/* === Sabor === */}
            <div className="review-section">
              <h2>Sabor y Calidad</h2>
              <StarRating
                count={5}
                value={saborRating}
                onChange={setSaborRating}
              />

              {/* Aparece el textarea apenas se haga clic en alguna estrella */}
              {saborRating > 0 && (
                <textarea
                  placeholder="¿Qué te pareció el sabor de tu comida?"
                  value={saborComment}
                  onChange={(e) => setSaborComment(e.target.value)}
                />
              )}

              {saborRating >= 4 && (
                <>
                  <p>¿Cuál fue tu plato favorito?</p>
                  <div className="vertical-list">
                    {receipt?.selections?.map(
                      (selection: any, index: number) => (
                        <div
                          key={index}
                          className={`list-item ${
                            selectedFavorite === index
                              ? 'selected favorite'
                              : ''
                          }`}
                          onClick={() => setSelectedFavorite(index)}
                        >
                          {getSelectionLine(selection)}
                          {selectedFavorite === index && (
                            <FaStar className="icon" />
                          )}
                        </div>
                      )
                    )}
                  </div>
                </>
              )}

              {saborRating > 0 && saborRating <= 3 && (
                <>
                  <p>¿Qué plato no cumplió tus expectativas?</p>
                  <div className="vertical-list">
                    {receipt?.selections?.map(
                      (selection: any, index: number) => (
                        <div
                          key={index}
                          className={`list-item ${
                            selectedWorst === index ? 'selected worst' : ''
                          }`}
                          onClick={() => setSelectedWorst(index)}
                        >
                          {getSelectionLine(selection)}
                          {selectedWorst === index && (
                            <FaThumbsDown className="icon" />
                          )}
                        </div>
                      )
                    )}
                  </div>
                </>
              )}
            </div>

            {/* === Experiencia === */}
            <div className="review-section">
              <h2>Experiencia General</h2>
              <StarRating
                count={5}
                value={experienciaRating}
                onChange={setExperienciaRating}
              />
              {/* Aparece el textarea apenas se haga clic en alguna estrella */}
              {experienciaRating > 0 && (
                <textarea
                  placeholder="Compártenos tu experiencia general con nosotros..."
                  value={experienciaComment}
                  onChange={(e) =>
                    setExperienciaComment(e.target.value)
                  }
                />
              )}
            </div>

            {/* === Botón Enviar, solo habilitado si canSubmit === */}
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className={canSubmit ? 'submit-button' : 'submit-button disabled-button'}
            >
              Enviar mi reseña <FaPaperPlane />
            </button>
          </>
        )}

        <div className="restaurant-site-powered">
          <span>Powered by </span>
          <img
            src={`${process.env.PUBLIC_URL}/logos/Recurso 49MEDIOPUNTO.png`}
            alt="Mediopunto Logo"
            className="powered-logo"
          />
          <span>MedioPunto</span>
        </div>
      </div>
    </div>
  );
};

export default ClientReviews;
